import React, { useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { UILoader } from '@futrli/ui-components';
import { workspaceByIdSelector } from '@futrli/shared-services';
import { useLocalisedConstantsContext } from '@futrli/localisation/core/localised-constants-context';

import { usePredictionsInfoContext } from '../../context/predictions-info-context';
import { OTHER_PL_CATEGORIES, StoreState, VIEW_TYPES } from '../../../../core/types';
import { CATEGORY_NAME_INDEXES, REPORT_SECTION } from '../../../../core/constants';
import { UserGridSettingsContext } from '../../reports/user-grid-settings/user-grid-settings-context';
import { ProfitLossPredictionsPageWrapper } from '../profit-loss-predictions-page-wrapper';
import { getConsolidatedGridInfo } from '../util-fns/consolidation-util-fns';
import { useConsolidationDataContext } from '../../../../predict-management/context/consolidation-data-context';
import { ForecastUpdating } from '../../components/forecast-updating';

export const ConsolidationOtherPL = () => {
  const intl = useIntl();
  const { workspaceId: tempWorkspaceId } = useParams();

  const workspaceId = tempWorkspaceId || '';

  const {
    category,
    consolidatedForecastReport,
    forecast,
    isLoading,
    periodType,
    totalCashPredictRow,
    viewType
  } = usePredictionsInfoContext();

  const workspace = useSelector((state: StoreState) => workspaceByIdSelector(state.normalised, workspaceId));

  const { nodeStaticIds } = useLocalisedConstantsContext();

  const { consolidation } = useConsolidationDataContext();

  const { expandedRows, rowHeights, getShowZeroValueAccounts } = useContext(UserGridSettingsContext);

  const viewOptions = useMemo(() => {
    return [
      {
        name: intl.formatMessage({
          id: 'predict.accruals_pl_view',
          defaultMessage: 'Accruals (P&L) view'
        }),
        value: VIEW_TYPES.ACCRUALS_PL
      },
      {
        name: intl.formatMessage({
          id: 'predict.cash_flow_view',
          defaultMessage: 'Cash flow view'
        }),
        value: VIEW_TYPES.CASH_FLOW
      }
    ];
  }, [intl]);

  const categoryOptions = useMemo(() => {
    return [
      {
        name: intl.formatMessage({
          id: 'financial_fields.category.other_income',
          defaultMessage: 'Other Income'
        }),
        value: OTHER_PL_CATEGORIES.OTHER_INCOME
      },
      {
        name: intl.formatMessage({
          id: 'financial_fields.category.other_expenses',
          defaultMessage: 'Other Expenses'
        }),
        value: OTHER_PL_CATEGORIES.OTHER_EXPENSES
      }
    ];
  }, [intl]);

  const { dataGridRows, totalRow } = useMemo(() => {
    return getConsolidatedGridInfo(
      intl,
      nodeStaticIds,
      viewType === VIEW_TYPES.CASH_FLOW ? REPORT_SECTION.CashFlow : REPORT_SECTION.ProfitLoss,
      (category as CATEGORY_NAME_INDEXES) || CATEGORY_NAME_INDEXES.other_income,
      workspace,
      expandedRows,
      periodType,
      consolidatedForecastReport,
      consolidation,
      getShowZeroValueAccounts(forecast?.id || '')
    );
  }, [
    intl,
    nodeStaticIds,
    consolidation,
    consolidatedForecastReport,
    expandedRows,
    periodType,
    viewType,
    workspace,
    category,
    getShowZeroValueAccounts,
    forecast?.id
  ]);

  if (!consolidatedForecastReport && isLoading) {
    return <UILoader isFull />;
  }

  return (
    <>
      <ProfitLossPredictionsPageWrapper
        reportColumns={
          viewType === VIEW_TYPES.CASH_FLOW
            ? consolidatedForecastReport?.data.cash_flow_columns
            : consolidatedForecastReport?.data.columns
        }
        rowHeights={rowHeights}
        viewOptions={viewOptions}
        accountRows={dataGridRows}
        totalRow={totalRow}
        category={
          category === OTHER_PL_CATEGORIES.OTHER_EXPENSES
            ? CATEGORY_NAME_INDEXES.other_expenses
            : CATEGORY_NAME_INDEXES.other_income
        }
        categoryOptions={categoryOptions}
        totalCashPredictRow={totalCashPredictRow}
      />
      <ForecastUpdating show={!!isLoading} />
    </>
  );
};
